import type { QuotationInvoiceCustomerListItem } from "@/modules/quotation-invoice/domain/types";
import type {
    CreateCustomerInput,
    UpdateCustomerInput,
} from "@/modules/quotation-invoice/presentation/api/client";
import {
    Button,
    FormCheckbox,
    FormInput,
    FormRoot,
    FormSelect,
    FormTextarea,
} from "@devhop/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import * as RHF from "react-hook-form";
import { z } from "zod";

/** ຄ່າຟອມສຳລັບ "ບໍ່ມີຫມວດຫມູ່" — ສົ່ງ null ໄປ API */
const CUSTOMER_FORM_NO_CATEGORY = "_none";

const CustomerFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ລູກຄ້າ"),
  address: z.string().nullable().optional(),
  contact: z.string().nullable().optional(),
  taxId: z.string().nullable().optional(),
  categoryId: z.string().optional(),
  isActive: z.boolean().optional(),
});

type CustomerFormValues = z.input<typeof CustomerFormSchema>;

export function CustomerForm({
  categoryOptions = [],
  initialValues,
  onSubmit,
  submitting,
}: {
  /** ປະເພດລູກຄ້າທີ່ໃຊ້ງານຢູ່ (ບໍ່ລວມຕົວເລືອກ "ບໍ່ມີຫມວດຫມູ່") */
  categoryOptions?: { value: string; label: string }[];
  initialValues?: Partial<CustomerFormValues> | QuotationInvoiceCustomerListItem;
  onSubmit: (values: CreateCustomerInput | UpdateCustomerInput) => void;
  submitting?: boolean;
}) {
  const categoryFieldOptions = [
    { value: CUSTOMER_FORM_NO_CATEGORY, label: "ບໍ່ມີຫມວດຫມູ່" },
    ...categoryOptions,
  ];

  const defaultValues: CustomerFormValues = {
    name: "",
    address: "",
    contact: "",
    taxId: "",
    categoryId: CUSTOMER_FORM_NO_CATEGORY,
    isActive: true,
    ...(initialValues && "id" in initialValues
      ? {
          name: initialValues.name,
          address: initialValues.address ?? "",
          contact: initialValues.contact ?? "",
          taxId: initialValues.taxId ?? "",
          categoryId:
            initialValues.categoryId ?? CUSTOMER_FORM_NO_CATEGORY,
          isActive: initialValues.isActive,
        }
      : initialValues),
  };

  const methods = RHF.useForm<CustomerFormValues>({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues,
  });

  return (
    <FormRoot<CustomerFormValues>
      methods={methods}
      onSubmit={(vals) => {
        const categoryId =
          !vals.categoryId || vals.categoryId === CUSTOMER_FORM_NO_CATEGORY
            ? null
            : vals.categoryId;
        const base = {
          name: vals.name.trim(),
          address: vals.address?.trim() || null,
          contact: vals.contact?.trim() || null,
          taxId: vals.taxId?.trim() || null,
          categoryId,
        };
        if (initialValues && "id" in initialValues && vals.isActive !== undefined) {
          onSubmit({ ...base, isActive: vals.isActive });
        } else {
          onSubmit(base);
        }
      }}
      className="space-y-4"
    >
      <FormInput
        name="name"
        label="ຊື່ລູກຄ້າ"
        placeholder="ຕົວຢ່າງ: ບໍລິສັດ ກັບ ຈຳກັດ"
        requiredMark
      />
      <FormSelect
        name="categoryId"
        label="ຫມວດຫມູ່ລູກຄ້າ"
        options={categoryFieldOptions}
      />
      <FormTextarea
        name="address"
        label="ທີ່ຢູ່"
        placeholder="ບໍ່ບັງຄັບ"
        rows={2}
      />
      <FormInput
        name="contact"
        label="ຕິດຕໍ່ (ເບີ / ອີເມວ)"
        placeholder="ບໍ່ບັງຄັບ"
      />
      <FormInput
        name="taxId"
        label="ເລກປະຈຳຕົວຜູ້ຊື້ (ຖ້າມີ)"
        placeholder="ບໍ່ບັງຄັບ"
      />
      {initialValues && "id" in initialValues && (
        <FormCheckbox name="isActive" label="ໃຊ້ງານຢູ່" />
      )}
      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
